import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { errMsg } from './salaryApi';

const EMPTY = { password: '', pin: '', confirm_pin: '' };

export const SalaryPinCard = ({ status, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const digits = (v) => v.replace(/\D/g, '').slice(0, 6);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post('/me/salary-pin', form);
      toast.success(r.data?.message || 'Salary PIN saved');
      setOpen(false); setForm(EMPTY); onChanged?.();
    } catch (e) { toast.error(errMsg(e, 'Could not save PIN')); onChanged?.(); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="salary-pin-card" className="border-dashed">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"><KeyRound size={18} /></div>
          <div>
            <div className="font-semibold text-sm" data-testid="salary-pin-status">
              {status?.is_set ? 'Salary PIN is set' : 'Create your Salary PIN'}
            </div>
            <p className="text-xs text-muted-foreground">
              Used only to confirm you received a payment. Different from your POS PIN. HR and admins cannot see it.
            </p>
            {status?.locked && (
              <p className="text-xs text-rose-600 mt-1 flex items-center gap-1" data-testid="salary-pin-locked">
                <Lock size={11} /> Locked after too many attempts until {new Date(status.locked_until).toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
        <Button size="sm" variant={status?.is_set ? 'outline' : 'default'} onClick={() => setOpen(true)} data-testid="salary-pin-open-btn">
          {status?.is_set ? 'Change / reset PIN' : 'Create PIN'}
        </Button>
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="salary-pin-dialog">
          <DialogHeader><DialogTitle>{status?.is_set ? 'Change Salary PIN' : 'Create Salary PIN'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Your login password</Label>
              <Input type="password" autoComplete="current-password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="salary-pin-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">New PIN (4–6 digits)</Label>
                <Input type="password" inputMode="numeric" value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: digits(e.target.value) })} data-testid="salary-pin-new-input" />
              </div>
              <div>
                <Label className="text-xs">Repeat PIN</Label>
                <Input type="password" inputMode="numeric" value={form.confirm_pin}
                  onChange={(e) => setForm({ ...form, confirm_pin: digits(e.target.value) })} data-testid="salary-pin-confirm-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.password || form.pin.length < 4} data-testid="salary-pin-save-btn">Save PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
